import React, { useState, useEffect } from 'react';
import { X, Clock, DollarSign } from 'lucide-react';

export default function RequestReviewPopup({ open, onClose, onProceed }) {
  const [requirements, setRequirements] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setRequirements("");
    } else {
      setTimeout(() => setIsVisible(false), 200);
    }
  }, [open]);

  if (!isVisible && !open) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className={`
        relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 transform transition-all duration-300 
        ${open ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}
      `}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 p-1 rounded-full"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Expert Review</h2>
          <p className="text-gray-500 text-sm">
            Get a human expert to review and refine your generated dataset as per your requirements.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
            <div className="p-2 bg-white rounded-lg shadow-sm text-blue-600">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider">Estimated Pricing</p>
              <p className="text-sm font-medium text-blue-700">Dynamic <span className="text-blue-500 font-normal">(Based on effort)</span></p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
            <div className="p-2 bg-white rounded-lg shadow-sm text-green-600">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-semibold text-green-900 uppercase tracking-wider">Estimated Response Time</p>
              <p className="text-sm font-medium text-green-700">Within 24 hours</p>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">Describe the changes needed</label>
          <textarea
            rows={4}
            value={requirements}
            onChange={(e) => setRequirements(e.target.value)}
            placeholder="E.g., The tone should be more formal, check for specific factual errors..."
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none placeholder-gray-400"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={() => onProceed("review", requirements)}
            className="flex-[2] px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-xl shadow-md shadow-blue-200 transition-all duration-200"
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
}
